import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from './api';

const ModulesContext = createContext(null);

export function ModulesProvider({ children }) {
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadEntitlements = useCallback(async () => {
    try {
      const res = await apiClient.get('/company/entitlements');
      setEntitlements(res.data);
    } catch {
      // Fallback: all active
      setEntitlements({
        plan: { key: 'enterprise', label: 'Enterprise', max_projects: 999, max_scenarios: 999, max_users: 999, features: ['all'] },
        modules: { financial: true, market: true, construction: true },
        trial: { is_trial: false, expired: false, ends_at: null },
        usage: { projects: 0, projects_limit: 999, projects_remaining: 999, users: 0, users_limit: 999 },
        permissions: { can_manage_users: true, can_manage_settings: true, can_manage_modules: true, can_create_projects: true, can_run_analysis: true, can_edit_scenarios: true, can_view_reports: true, can_delete_projects: true, can_transfer_ownership: false },
        role: 'owner',
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadEntitlements(); }, [loadEntitlements]);

  const isActive = useCallback((mod) => {
    const m = entitlements?.modules?.[mod];
    if (!m) return mod === 'financial'; // Financial always active
    return m.active !== false;
  }, [entitlements]);
  const hasPermission = useCallback((perm) => entitlements?.permissions?.[perm] !== false, [entitlements]);
  const hasFeature = useCallback((feat) => {
    const features = entitlements?.features || [];
    return features.includes('all') || features.includes(feat);
  }, [entitlements]);
  const canCreate = useCallback((resource) => {
    if (!entitlements) return true;
    if (resource === 'project') return entitlements.usage.projects_remaining > 0;
    return true;
  }, [entitlements]);

  const value = {
    // Modules
    modules: entitlements?.modules || { financial: { active: true, type: 'core' }, market: { active: true, type: 'addon' }, construction: { active: true, type: 'addon' } },
    isActive,
    // Plan
    plan: entitlements?.plan || {},
    // Trial
    isTrial: entitlements?.trial?.is_trial || false,
    trialExpired: entitlements?.trial?.expired || false,
    trialEnds: entitlements?.trial?.ends_at,
    // Permissions
    permissions: entitlements?.permissions || {},
    hasPermission,
    // Features
    hasFeature,
    // Usage & limits
    usage: entitlements?.usage || {},
    canCreate,
    // Role
    role: entitlements?.role || 'viewer',
    // Utils
    loading,
    refresh: loadEntitlements,
    entitlements,
  };

  return (
    <ModulesContext.Provider value={value}>
      {children}
    </ModulesContext.Provider>
  );
}

export function useModules() {
  const ctx = useContext(ModulesContext);
  if (!ctx) return {
    modules: { financial: true, market: true, construction: true },
    isActive: () => true, hasPermission: () => true, hasFeature: () => true, canCreate: () => true,
    plan: {}, isTrial: false, trialExpired: false, usage: {}, permissions: {}, role: 'owner', loading: false,
  };
  return ctx;
}
